// duprApiCore.ts
import axios, { AxiosError, AxiosInstance, AxiosRequestHeaders, InternalAxiosRequestConfig } from 'axios'
import { Configuration } from '@/app/commons/generated'
import * as apis from '@/app/commons/generated/api'
import { MFA_CLIENT_CAPABILITIES } from '@/constants/Common'

export type TokenBundle = { accessToken: string; refreshToken?: string }

type DuprApi = {
  axios: AxiosInstance
  auth: apis.AuthenticationApi
  user: apis.UserApi
  matches: apis.MatchesApi
  featureUsage: apis.FeatureUsageApi
  badges: apis.BadgesApi
  dispose: () => void
}

type InitOpts = {
  basePath: string
  getAccessToken: () => string | undefined
  getRefreshToken: () => string | undefined
  writeTokens?: (tokens: TokenBundle) => void
  onRefreshFailure?: (err: unknown) => void
}

const REFRESH_VERSION = 'v2.0'

// Refresh debug hook
export type RefreshDebugEvent =
  | { type: 'success'; res: unknown; tokens: TokenBundle }
  | { type: 'error'; error: unknown }

let refreshDebugListener: ((e: RefreshDebugEvent) => void) | null = null
export function setRefreshDebugListener(fn: ((e: RefreshDebugEvent) => void) | null): void {
  refreshDebugListener = fn
}

function createAxios(baseURL: string) {
  return axios.create({
    baseURL,
    timeout: 30000,
    // Send the HttpOnly first-party session cookie (same-site with the API per environment).
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      // The strong second factors this browser client can perform. The server offers the
      // intersection of (what the user enrolled) and (what this client can do); advisory only.
      // Supersedes the legacy `mfa-challenge` token, which advertised no strong capability.
      'X-DUPR-Client-Capabilities': MFA_CLIENT_CAPABILITIES,
    },
  })
}
// Cookie-based refresh no longer parses tokens out of the body (there are none), so the old
// extractTokensV2 helper is gone.
type RetriableConfig<D = unknown> = InternalAxiosRequestConfig<D> & {
  _retry?: boolean
}
function attachAuthHeader<D = unknown>(
  config: InternalAxiosRequestConfig<D>,
  token?: string
): InternalAxiosRequestConfig<D> {
  if (!token) return config
  const headers: AxiosRequestHeaders = (config.headers ?? {}) as AxiosRequestHeaders
  headers.Authorization = `Bearer ${token}`
  config.headers = headers
  return config
}
const is401 = (err: AxiosError) => err.response?.status === 401 && !!err.config
function singleFlight<T>() {
  let inflight: Promise<T> | null = null
  return {
    run(fn: () => Promise<T>) {
      if (!inflight) inflight = fn().finally(() => setTimeout(() => (inflight = null), 0))
      return inflight
    },
  }
}

let singleton: DuprApi | null = null

export function initDuprApi(opts: InitOpts): DuprApi {
  if (singleton) return singleton

  const axiosAuthd = createAxios(opts.basePath)
  const axiosRefresh = createAxios(opts.basePath)

  const configuration = new Configuration({ basePath: opts.basePath })

  const auth = new apis.AuthenticationApi(configuration, undefined, axiosAuthd)
  const user = new apis.UserApi(configuration, undefined, axiosAuthd)
  const matches = new apis.MatchesApi(configuration, undefined, axiosAuthd)
  const featureUsage = new apis.FeatureUsageApi(configuration, undefined, axiosAuthd)
  const badges = new apis.BadgesApi(configuration, undefined, axiosAuthd)


  const flight = singleFlight<void>()

  // No bearer is attached: the first-party session authenticates via the HttpOnly cookie (sent
  // because the axios instance uses withCredentials). attachAuthHeader stays a no-op — getAccessToken
  // returns '' now — but the hook is kept so a future non-cookie client could reuse it.
  const reqId = axiosAuthd.interceptors.request.use((cfg) => attachAuthHeader(cfg, opts.getAccessToken()))

  const resId = axiosAuthd.interceptors.response.use(
    (r) => r,
    async (error: AxiosError) => {
      if (!is401(error)) return Promise.reject(error)

      const original = error.config as RetriableConfig
      if (!original || original._retry) return Promise.reject(error)
      original._retry = true

      try {
        await flight.run(async () => {
          // First-party refresh is the cookie-only POST /auth/{v}/refresh, segregated from the
          // partner GET+`x-refresh-token`. The HttpOnly refresh cookie rides withCredentials and the
          // server rotates the session back into cookies; no token crosses JS, so there is nothing
          // to extract — we just retry the original request once the cookie is refreshed. Called on
          // axiosRefresh (a separate instance with no 401-retry interceptor) so it cannot recurse,
          // and directly rather than via the generated client, which models the partner GET.
          const res = await axiosRefresh.post(`/auth/${REFRESH_VERSION}/refresh`)
          refreshDebugListener?.({ type: 'success', res, tokens: { accessToken: '' } })
        })

        return axiosAuthd.request(original)
      } catch (e) {
        refreshDebugListener?.({ type: 'error', error: e })
        opts.onRefreshFailure?.(e)
        return Promise.reject(e)
      }
    }
  )

  singleton = {
    axios: axiosAuthd,
    auth,
    user,
    matches,
    featureUsage,
    badges,
    dispose: () => {
      axiosAuthd.interceptors.request.eject(reqId)
      axiosAuthd.interceptors.response.eject(resId)
      singleton = null
    },
  }

  return singleton
}

export function getDuprApi(): DuprApi {
  if (!singleton) {
    throw new Error('duprApi is not initialized. Call initDuprApi(...) first.')
  }
  return singleton
}
